import { format } from "date-fns"
import { observer } from "mobx-react-lite"

import type { Token } from "@/domains/core"
import type { SortBy } from "@/domains/lock-page"
import type { LockTableRowData } from "@/domains/lock-page/components/card/0.story-mocks"
import { Button } from "@/components/button"
import { Checkbox } from "@/components/checkbox"
import { ShowWhenFalse, ShowWhenTrue } from "@/components/conditionals"
import CloseDialogX from "@/components/icons/close-dialog-x"
import { Separator } from "@/components/separator"
import { Sheet, SheetClose, SheetContent, SheetTrigger } from "@/components/sheet"
import { SortableTableHeader } from "@/components/sortable-table-header"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableHeaderRow, TableRow } from "@/components/table"
import { TokenIcon } from "@/components/token-icon"
import { Lozenge } from "@/domains/common/components/lozenge"
import { CaretDownIcon } from "@/domains/icons"
import { cn, formatCurrency } from "@/domains/library"

interface NftStatusProps {
  hasExpired: boolean
  hasVotedThisEpoch: boolean
  isReset: boolean
}

const NftStatus = observer(({ hasExpired, hasVotedThisEpoch, isReset }: NftStatusProps) => {
  if (hasExpired) {
    return (
      <Lozenge color="error" size="xs">
        Expired
      </Lozenge>
    )
  }

  if (hasVotedThisEpoch) {
    return (
      <Lozenge color="error" size="xs">
        Voted
      </Lozenge>
    )
  }

  if (isReset === false) {
    return (
      <Lozenge color="blue" size="xs">
        Needs Reset
      </Lozenge>
    )
  }

  if (isReset === true) {
    return (
      <Lozenge color="blue" size="xs">
        Reset
      </Lozenge>
    )
  }

  return null
})

export interface MergeNftSelectorProps {
  nftId: number
  selectedNftId: number | null
  allOtherNFTs: Array<LockTableRowData>
  isModalOpen: boolean
  governanceToken: Token
  vestedTokenSymbol: string
  sortBy?: SortBy
  onSelectNft: (nftId: number) => void
  onDeselectNft: () => void
  onModalOpenChange: (open: boolean) => void
  onSortChange?: (sortBy: SortBy) => void
}

export const MergeNftSelector = ({
  nftId,
  selectedNftId,
  allOtherNFTs,
  isModalOpen,
  governanceToken,
  vestedTokenSymbol,
  sortBy,
  onSelectNft,
  onDeselectNft,
  onModalOpenChange,
  onSortChange,
}: MergeNftSelectorProps) => {
  const selectedNft = allOtherNFTs.find((v) => v.id === selectedNftId)
  const hasViableSelection = !!selectedNft

  const handleSort = (key: string) => {
    if (!onSortChange || !sortBy) return
    onSortChange({
      key,
      direction: sortBy.direction === "asc" ? "desc" : "asc",
      type: "number",
    })
  }

  return (
    <Sheet modal={true} open={isModalOpen} onOpenChange={onModalOpenChange}>
      <SheetTrigger asChild>
        <Button variant="primary" className="flex items-center gap-2">
          {selectedNft ? (
            <>
              <div className="flex items-center gap-2">#{selectedNft.id}</div>
              <CaretDownIcon className="h-[12px] w-[14px]" />
            </>
          ) : (
            <>
              Select an NFT
              <CaretDownIcon className="h-[12px] w-[14px]" />
            </>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent side="nft_dialog" className="flex h-full flex-col p-0">
        <div className="flex flex-col gap-1 px-2 py-4 sm:p-4">
          <div className="flex items-center justify-between">
            <p className="text-xl font-medium">Select an NFT</p>
            <SheetClose>
              <CloseDialogX onClick={() => {}} />
            </SheetClose>
          </div>
          <div className="mb-6 text-sm text-t200">
            Select an NFT to merge into <span className="text-t100">#{nftId}</span>
          </div>
          <Separator
            className="relative h-0 w-full overflow-visible rounded-md border border-solid
          border-p400-light after:pointer-events-none after:absolute after:inset-[-1px] after:z-[1] 
          after:rounded-md after:shadow-[inset_0.5px_0.5px_0.5px_0px_hsla(220,43%,5%,1)] after:content-['']"
            decorative
          />
        </div>
        <div className="flex flex-grow flex-col overflow-hidden">
          <div className="h-full overflow-y-auto px-0 sm:px-4">
            <Table className="overflow-hidden overflow-y-auto">
              <TableHead>
                <TableHeaderRow>
                  <TableHeader className="pr-4 text-right" classNameInner="justify-center" isSortDisabled={true}>
                    &nbsp;
                  </TableHeader>
                  {sortBy && onSortChange ? (
                    <>
                      <SortableTableHeader
                        sortBy={sortBy}
                        sortColumn="id"
                        className=""
                        classNameInner="justify-start"
                        onClick={() => handleSort("id")}
                      >
                        NFT
                      </SortableTableHeader>
                      <SortableTableHeader
                        className="text-right"
                        classNameInner="justify-end"
                        sortBy={sortBy}
                        sortColumn="lockEnds"
                        onClick={() => handleSort("lockEnds")}
                      >
                        Expires
                      </SortableTableHeader>
                      <SortableTableHeader
                        className="w-[23%] text-right"
                        classNameInner="justify-end"
                        sortBy={sortBy}
                        sortColumn="lockAmount"
                        onClick={() => handleSort("lockAmount")}
                      >
                        Locked
                      </SortableTableHeader>
                      <SortableTableHeader
                        className="w-[23%] text-right"
                        classNameInner="justify-end"
                        sortBy={sortBy}
                        sortColumn="lockValue"
                        onClick={() => handleSort("lockValue")}
                      >
                        Voting Power
                      </SortableTableHeader>
                    </>
                  ) : (
                    <>
                      <TableHeader className="" classNameInner="justify-start">
                        NFT
                      </TableHeader>
                      <TableHeader className="text-right" classNameInner="justify-end">
                        Expires
                      </TableHeader>
                      <TableHeader className="w-[23%] text-right" classNameInner="justify-end">
                        Locked
                      </TableHeader>
                      <TableHeader className="w-[23%] text-right" classNameInner="justify-end">
                        Voting Power
                      </TableHeader>
                    </>
                  )}
                  <TableHeader classNameInner="justify-center" isSortDisabled={true}>
                    Status
                  </TableHeader>
                </TableHeaderRow>
              </TableHead>
              <TableBody>
                <ShowWhenTrue when={allOtherNFTs.length === 0}>
                  <TableRow>
                    <TableCell colSpan={6} className="py-6 text-center">
                      No veNFTs found in your account other than <span className="text-t100">#{nftId}</span>
                    </TableCell>
                  </TableRow>
                </ShowWhenTrue>
                <ShowWhenTrue when={allOtherNFTs.length > 0}>
                  {allOtherNFTs.map((v) => {
                    const hasVotedThisEpoch = v.hasVotedThisEpoch
                    const hasExpired = v.hasExpired
                    const isSelectedNft = v.id === selectedNftId
                    const canSelect = !hasVotedThisEpoch && !hasExpired

                    return (
                      <TableRow
                        className={cn(isSelectedNft ? "bg-p400/50" : "bg-transparent", "transition-colors")}
                        key={v.id}
                      >
                        <TableCell>
                          <div className="relative flex justify-center">
                            <Checkbox
                              id={String(v.id)}
                              checked={isSelectedNft}
                              onChange={() => {
                                if (isSelectedNft) {
                                  onDeselectNft()
                                } else {
                                  onSelectNft(v.id)
                                }
                              }}
                              disabled={!canSelect}
                            />
                          </div>
                        </TableCell>
                        <TableCell className="text-t100">
                          <span className="hidden md:inline-block">NFT</span> #{v.id}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col items-end justify-end">
                            <ShowWhenTrue when={hasExpired}>
                              <Lozenge color="error" size="xs">
                                Expired
                              </Lozenge>
                            </ShowWhenTrue>
                            <ShowWhenFalse when={hasExpired}>
                              <p className="text-t100">{format(v.lockEnds * 1000, "MMM do, yyyy")}</p>
                            </ShowWhenFalse>
                          </div>
                        </TableCell>

                        <TableCell className="text-end">
                          <p className="text-t100">{formatCurrency(v.lockAmount)}</p>
                          <div className="hidden justify-end gap-1 text-xs sm:flex">
                            <TokenIcon token={governanceToken} square={16} calledFrom="MergeNft" />
                            <p>{governanceToken.symbol}</p>
                          </div>
                        </TableCell>

                        <TableCell className="text-end">
                          <p className="text-t100">{formatCurrency(v.lockValue)}</p>
                          <div className="hidden justify-end gap-1 text-xs sm:flex">
                            <TokenIcon token={governanceToken} square={16} calledFrom="MergeNft" />
                            <p>{vestedTokenSymbol}</p>
                          </div>
                        </TableCell>

                        <TableCell>
                          <div className="flex flex-col items-center justify-start gap-1 text-xs">
                            <NftStatus
                              hasExpired={hasExpired}
                              hasVotedThisEpoch={hasVotedThisEpoch}
                              isReset={v.isReset}
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </ShowWhenTrue>
              </TableBody>
            </Table>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}
